import { provide } from '@lit/context';
import { LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ContentDeliveryClient } from '../clients/content-delivery-client';
import { ContentDeliveryMicroservice } from '../clients/content-delivery-microservice';
import { ContentDeliveryMonoRails } from '../clients/content-delivery-monorails';
import { LearnerProgressionClient } from '../clients/learner-progression-client';
import { SkillsClient } from '../clients/skills-client';
import { DataService } from '../services/data_service';
import { ModalService } from '../services/luds-modal-service';
import { ToastService } from '../services/luds-toast-service';
import { ContainerMixin } from '../shared/mixins/container.mixin';
import { EntryComponentConfig, PreloadResource } from '../shared/types/common.model';
import { ConsoleLogger } from '../shared/util/console-logger';
import { safeParseJson } from '../shared/util/safe-parse-json';
import { configContext } from './config.context';
import { contentDeliveryClientContext } from './content-delivery-client.context';
import { contentDetailsClientContext } from './content-details-client.context';
import { dataServiceContext } from './data-service.context';
import { learnerProgressionClientContext } from './learner-progression.client.context';
import { Locale, localeContext } from './locale.context';
import { Logger, loggerContext } from './logger.context';
import { modalServiceContext } from './modal-service.context';
import { skillsClientContext } from './skills-client.context';
import { toastServiceContext } from './toast-service.context';
import { ContentDetailsClient } from '../clients/content-details-client';

@customElement('lua-provider')
export class LuaProvider extends ContainerMixin(LitElement) {
  @property({ type: String })
  config?: string;

  @property({ attribute: false })
  extendedConfig?: {
    reattemptNewFlow?: boolean; // TODO remove this when refactoring readConfig
  };

  @provide({ context: loggerContext })
  @property({ attribute: false })
  logger: Logger = new ConsoleLogger();

  @provide({ context: dataServiceContext })
  @property({ attribute: false })
  dataService: DataService;

  @provide({ context: contentDeliveryClientContext })
  @property({ attribute: false })
  contentDeliveryClient: ContentDeliveryClient;

  @provide({ context: skillsClientContext })
  @property({ attribute: false })
  skillsClient: SkillsClient;

  @provide({ context: contentDetailsClientContext })
  @property({ attribute: false })
  contentDetailsClient: ContentDetailsClient;

  @provide({ context: learnerProgressionClientContext })
  @property({ attribute: false })
  learnerProgressionClient: LearnerProgressionClient;

  @provide({ context: modalServiceContext })
  @property({ attribute: false })
  modalService: ModalService;

  @provide({ context: toastServiceContext })
  @property({ attribute: false })
  toastService: ToastService;

  @provide({ context: localeContext })
  @property({ attribute: false })
  locale: Locale;

  @provide({ context: configContext })
  @state()
  private internalConfig: EntryComponentConfig;

  override connectedCallback(): void {
    super.connectedCallback();

    this.readConfig();

    this.configureDataService();

    this.modalService = ModalService.getInstance();
    this.toastService = ToastService.getInstance();
  }

  // FIXME: This approach can lead to a race condition if readConfig() is called before the DOM is fully ready,
  // resulting in configElement being null and default settings being used unintentionally.
  // Better refactoring to use properties passed directly to the course-player component. (extended config implementation)
  private readConfig(): void {
    const configElement = document.getElementById(this.config);

    if (!configElement) {
      this.logger.warn('LUA Config not found, using default settings');
    }

    const configJson = this.config && configElement ? configElement.textContent : '{}';

    const defaultSettings: EntryComponentConfig = {
      baseUrl: '',
      flags: {
        isPortal: true,
        ignoreScormCheck: false,
      },
    };

    this.internalConfig = {
      ...defaultSettings,
      ...(safeParseJson<EntryComponentConfig>(configJson) ?? {}),
      ...this.extendedConfig,
    };

    this.logger.info(this.internalConfig);
  }

  private async configureDataService() {
    const ContentDeliveryClient = this.internalConfig?.flags?.isPortal
      ? ContentDeliveryMonoRails
      : ContentDeliveryMicroservice;

    this.dataService = DataService.getInstance(this.internalConfig.baseUrl);
    this.contentDeliveryClient = new ContentDeliveryClient(this.internalConfig);
    this.learnerProgressionClient = new LearnerProgressionClient(this.internalConfig);
    this.skillsClient = new SkillsClient(this.internalConfig);
    this.contentDetailsClient = new ContentDetailsClient(this.internalConfig);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-provider': LuaProvider;
  }

  interface WindowEventMap {
    'lua-preload-resource': CustomEvent<PreloadResource>;
  }
}
